import Axios from "axios";
import Auth from "../auth";
import { gatewayURL } from "../config";
import chatflowConfig from "./config";

class ChatflowApi {
  constructor() {
    this._client = null;
    this._refreshPromise = null;
  }

  _getClient() {
    const { restUrl } = chatflowConfig;
    if (!this._client || this._client.defaults.baseURL !== restUrl) {
      this._client = Axios.create({
        baseURL: restUrl,
        headers: { "Content-Type": "application/json" },
        transformRequest: [
          function (data, headers) {
            headers["Authorization"] = `Bearer ${Auth.getToken()}`;
            if (headers["Content-Type"] === "application/json") {
              return JSON.stringify(data);
            }
            return data;
          },
        ],
      });

      this._client.interceptors.response.use(
        (response) => response,
        (error) => this._handleAuthError(error)
      );
    }
    return this._client;
  }

  _handleAuthError(error) {
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (!originalRequest || originalRequest._chatflowRetried) {
      return Promise.reject(error);
    }

    const refreshToken = Auth.getRefreshToken();
    if (!refreshToken) {
      return Promise.reject(error);
    }

    originalRequest._chatflowRetried = true;

    if (!this._refreshPromise) {
      this._refreshPromise = Axios.post(`${gatewayURL}/refresh-token`, { refreshToken })
        .then((response) => {
          Auth.setToken(response.data.token);
          Auth.setRefreshToken(response.data.refreshToken);
          return response.data.token;
        })
        .finally(() => {
          this._refreshPromise = null;
        });
    }

    return this._refreshPromise
      .then((token) => {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        return this._getClient().request(originalRequest);
      })
      .catch(() => Promise.reject(error));
  }

  async getFlows() {
    const { data } = await this._getClient().get("/flows");
    return data.data;
  }

  async getFlow(id) {
    const { data } = await this._getClient().get(`/flows/${id}`);
    return data.data;
  }

  async createFlow(payload) {
    const { data } = await this._getClient().post("/flows", payload);
    return data.data;
  }

  async updateFlow(id, payload) {
    const { data } = await this._getClient().patch(`/flows/${id}`, payload);
    return data.data;
  }

  async deleteFlow(id) {
    const { data } = await this._getClient().delete(`/flows/${id}`);
    return data.data;
  }

  async publishFlow(id) {
    const { data } = await this._getClient().post(`/flows/${id}/publish`);
    return data.data;
  }

  async getChannels() {
    const { data } = await this._getClient().get("/channels");
    return data.data;
  }

  async createTelegramChannel(payload) {
    const { data } = await this._getClient().post("/channels/telegram", payload);
    return data.data;
  }

  async createWhatsAppChannel(payload) {
    const { data } = await this._getClient().post("/channels/whatsapp", payload);
    return data.data;
  }

  async createWebChannel(payload) {
    const { data } = await this._getClient().post("/channels/web", payload);
    return data.data;
  }

  async updateChannel(id, payload) {
    const { data } = await this._getClient().patch(`/channels/${id}`, payload);
    return data.data;
  }

  async deleteChannel(id) {
    const { data } = await this._getClient().delete(`/channels/${id}`);
    return data.data;
  }

  async registerTelegramWebhook(channelId) {
    const { data } = await this._getClient().post(
      `/channels/${channelId}/telegram/register-webhook`
    );
    return data.data;
  }

  async getTelegramStatus(channelId) {
    const { data } = await this._getClient().get(`/channels/${channelId}/telegram/status`);
    return data.data;
  }

  async getConversations(params) {
    const { data } = await this._getClient().get("/conversations", { params });
    return data.data;
  }

  async getConversation(id, params = {}) {
    const { data } = await this._getClient().get(`/conversations/${id}`, { params });
    return data.data;
  }

  async resetConversation(id) {
    const { data } = await this._getClient().post(`/conversations/${id}/reset`);
    return data.data;
  }

  async sendConversationMessage(id, payload, operatorName, afterMessageId) {
    const body =
      typeof payload === "string"
        ? { text: payload }
        : payload?.message
          ? payload
          : { message: payload };
    if (operatorName) body.operatorName = operatorName;
    if (afterMessageId) body.afterMessageId = afterMessageId;
    const { data } = await this._getClient().post(`/conversations/${id}/messages`, body);
    return data.data;
  }

  async setConversationAgentMode(id, enabled, operatorName) {
    const body = { enabled: Boolean(enabled) };
    if (operatorName) body.operatorName = operatorName;
    const { data } = await this._getClient().post(`/conversations/${id}/agent-mode`, body);
    return data.data;
  }

  async getSettings() {
    const { data } = await this._getClient().get("/settings");
    return data.data;
  }

  async updateSettings(payload) {
    const { data } = await this._getClient().patch("/settings", payload);
    return data.data;
  }

  async getContacts(params) {
    const { data } = await this._getClient().get("/contacts", { params });
    return data.data;
  }

  async createContact(payload) {
    const { data } = await this._getClient().post("/contacts", payload);
    return data.data;
  }

  async updateContact(id, payload) {
    const { data } = await this._getClient().patch(`/contacts/${id}`, payload);
    return data.data;
  }

  async deliverAlert(payload) {
    const { data } = await this._getClient().post("/alerts/deliver", payload);
    return data.data;
  }
}

export default new ChatflowApi();
